import { useQueries, useQuery } from "@tanstack/react-query";

import * as api from "@nens/threedi/openapi-client";

import { RainRasters, RasterAggregateResult, RasterAggregateResults } from "../types/rasterRain";
import { getRasterStartMoment, getRasterStopMoment } from "../utils/rainRasterHelper";
import {
  getRasterAggregateUrl,
  rasterAggregateResultToMmPerDuration,
} from "../utils/rainRasterHelper";

export function useLizardAggregates(RAIN_RASTERS: RainRasters, modelPolygon: string) {
  const rainRasterArray = Object.entries(RAIN_RASTERS);
  const filteredRainRasterArray = rainRasterArray.filter((rainraster) => {
    return rainraster[1].isPublic === true;
  });
  const names = filteredRainRasterArray.map(([name, data]) => name);

  const urlQueries = useQueries({
    queries: filteredRainRasterArray.map(([name, data]) => {
      const startDate = (() => {
        const momentDate = getRasterStartMoment(data);
        momentDate.utc();
        return momentDate.format();
      })();
      const stopDate = (() => {
        const momentDate = getRasterStopMoment(data);
        momentDate.utc();
        return momentDate.format();
      })();
      const url = getRasterAggregateUrl(modelPolygon, data.uuid, startDate, stopDate, "D");
      const fetchAggregate = (): Promise<RasterAggregateResult | undefined> => {
        return fetch(url)
          .then((resp) => resp.json())
          .then((result: RasterAggregateResult) => {
            return rasterAggregateResultToMmPerDuration(result, data.frequency, data.unit);
          });
      };
      return {
        queryKey: [
          "rainrasteraggregates",
          name,
          modelPolygon,
          data.uuid,
          startDate.slice(0, -3),
          stopDate.slice(0, -3),
        ],
        queryFn: fetchAggregate,
        enabled: !!modelPolygon,
        retry: false,
      };
    }),
  });

  const results: RasterAggregateResults = {};
  urlQueries.forEach(({ data }, indx) => {
    if (data) {
      results[names[indx]] = data;
    }
  });

  return results;
}

export function useLizardAggregate(
  rasterUuid: string,
  modelPolygon: string,
  startDateTime: string,
  stopDateTime: string,
  // as described in the api docs here:
  // https://demo.lizard.net/api/v4/rasters/d6c2347d-7bd1-4d9d-a1f6-b342c865516f/zonal/?zonal_statistic=mean&geom=POLYGON((+4.655663105256908+52.59085747199397,+4.808619107856246+52.59085747199397,+4.808619107856246+52.697177256067306,+4.655663105256908+52.697177256067306,+4.655663105256908+52.59085747199397))&start=2022-08-11T12:11:29Z&stop=2022-08-22T12:11:29Z
  // parameters for "frequency" can be found here:
  // https://pandas.pydata.org/pandas-docs/stable/user_guide/timeseries.html#dateoffset-objects
  frequency: string,
  unit: api.RasterRainUnitsEnum
) {
  const url = getRasterAggregateUrl(
    modelPolygon,
    rasterUuid,
    startDateTime,
    stopDateTime,
    frequency
  );

  const fetchAggregate = (): Promise<RasterAggregateResult | undefined> => {
    return fetch(url)
      .then((resp) => resp.json())
      .then((result: RasterAggregateResult) => {
        return rasterAggregateResultToMmPerDuration(result, frequency, unit);
      });
  };

  return useQuery({
    queryKey: [
      "rainrasteraggregate",
      modelPolygon,
      rasterUuid,
      startDateTime.slice(0, -3),
      stopDateTime.slice(0, -3),
      frequency,
    ],
    queryFn: fetchAggregate,
    enabled: !!modelPolygon,
  });
}

export function useLizardAggregateInStepsOfDay(
  rasterUuid: string,
  modelPolygon: string,
  startDateTime: string,
  stopDateTime: string,
  // as described in the api docs here:
  // https://demo.lizard.net/api/v4/rasters/d6c2347d-7bd1-4d9d-a1f6-b342c865516f/zonal/?zonal_statistic=mean&geom=POLYGON((+4.655663105256908+52.59085747199397,+4.808619107856246+52.59085747199397,+4.808619107856246+52.697177256067306,+4.655663105256908+52.697177256067306,+4.655663105256908+52.59085747199397))&start=2022-08-11T12:11:29Z&stop=2022-08-22T12:11:29Z
  // parameters for "frequency" can be found here:
  // https://pandas.pydata.org/pandas-docs/stable/user_guide/timeseries.html#dateoffset-objects
  frequency: string,
  unit: api.RasterRainUnitsEnum
) {
  const startStopsIntervals: { start: string; stop: string }[] = [];
  const firstStop =
    getStartNextDayAsString(startDateTime) > stopDateTime
      ? stopDateTime
      : getStartNextDayAsString(startDateTime);
  startStopsIntervals.push({ start: startDateTime, stop: firstStop });

  let nextStop = firstStop;

  while (getStartNextDayAsString(nextStop) < stopDateTime) {
    startStopsIntervals.push({ start: nextStop, stop: getStartNextDayAsString(nextStop) });
    nextStop = getStartNextDayAsString(nextStop);
  }
  startStopsIntervals.push({ start: nextStop, stop: stopDateTime });

  const urlQueries = useQueries({
    queries: startStopsIntervals.map(({ start, stop }) => {
      const url = getRasterAggregateUrl(modelPolygon, rasterUuid, start, stop, frequency);
      const fetchAggregate = (): Promise<RasterAggregateResult | undefined> => {
        return fetch(url)
          .then((resp) => resp.json())
          .then((result: RasterAggregateResult) => {
            return rasterAggregateResultToMmPerDuration(result, frequency, unit);
          });
      };

      return {
        queryKey: [
          "rainrasteraggregaterecursive",
          modelPolygon,
          rasterUuid,
          start.slice(0, -3),
          stop.slice(0, -3),
        ],
        queryFn: fetchAggregate,
        enabled: !!modelPolygon,
        retry: false,
      };
    }),
  });

  const combinedResults: RasterAggregateResult = {
    results: [],
    message: "",
    status: 0,
  };

  urlQueries.forEach(({ data }, indx) => {
    if (data) {
      data.results?.forEach((result) => {
        if (combinedResults.results && combinedResults.results.length === 0) {
          combinedResults.results.push(result);
        } else if (
          combinedResults.results &&
          combinedResults.results[combinedResults.results?.length - 1]?.time !== result.time
        ) {
          combinedResults.results.push(result);
        }
      });
    }
  });

  return combinedResults;
}

const getStartNextDayAsString = (dateTime: string) => {
  const nextDay = new Date(dateTime);
  nextDay.setDate(nextDay.getDate() + 1);
  nextDay.setUTCHours(0, 0, 0, 0);
  return nextDay.toISOString();
};
